import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import {
    appendFieldError,
    createDropdown,
    createField,
    createTextareaField,
    disableFormControls,
    getDirectFieldError
} from '../forms/formFields';
import {createKeyValueMap} from '../forms/repeatableFields';
import {cloneValue} from '../forms/formState';
import {stringifyJsonFallbackValue} from '../forms/jsonFallbackField';

const bodyModeEmpty = 'EMPTY';
const bodyModeText = 'TEXT';
const bodyModeJson = 'JSON';

const httpMethods = ['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'];
const wellKnownRequestHeaders = [
    'Accept',
    'Accept-Charset',
    'Accept-Encoding',
    'Accept-Language',
    'Content-Encoding',
    'Content-Language',
    'User-Agent'
];

const defaultHttpRequest = {
    url: '',
    method: 'GET',
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    },
    body: null
};

const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]';
const hasOwnProperty = (value, property) => Object.prototype.hasOwnProperty.call(value, property);

const normalizeHeaders = headers => isPlainObject(headers) ? cloneValue(headers) : headers;

const normalizeHttpRequest = httpRequest => {
    if (!isPlainObject(httpRequest)) {
        return cloneValue(defaultHttpRequest);
    }

    const normalizedHttpRequest = {};
    if (hasOwnProperty(httpRequest, 'url')) {
        normalizedHttpRequest.url = httpRequest.url || '';
    }
    if (hasOwnProperty(httpRequest, 'method')) {
        normalizedHttpRequest.method = isEmpty(httpRequest.method) ? 'GET' : String(httpRequest.method).toUpperCase();
    }
    if (hasOwnProperty(httpRequest, 'headers')) {
        normalizedHttpRequest.headers = normalizeHeaders(httpRequest.headers);
    }
    if (hasOwnProperty(httpRequest, 'body')) {
        normalizedHttpRequest.body = cloneValue(httpRequest.body);
    }
    return normalizedHttpRequest;
};

const getDisplayMethod = httpRequest => isEmpty(httpRequest.method) ? 'GET' : String(httpRequest.method).toUpperCase();

const bodyModeFor = body => {
    if (body === null || typeof body === 'undefined') {
        return bodyModeEmpty;
    }

    return typeof body === 'string' ? bodyModeText : bodyModeJson;
};

const stringifyBody = body => {
    if (body === null || typeof body === 'undefined') {
        return '';
    }

    if (typeof body === 'string') {
        return body;
    }

    return stringifyJsonFallbackValue(body);
};

const parseJsonBody = value => {
    if (isEmpty(value)) {
        return {
            valid: true,
            body: null
        };
    }

    try {
        return {
            valid: true,
            body: JSON.parse(value)
        };
    } catch (e) {
        return {
            valid: false,
            body: null
        };
    }
};

const getResource = (mxResources, key) => mxResources.get(key);

const appendJsonExpressionHelpToContainer = (container, help) => {
    if (!help || container.querySelector('.json-expression-help-field')) {
        return;
    }

    const helpField = document.createElement('div');
    helpField.className = 'field json-expression-help-field';
    helpField.insertAdjacentHTML('beforeend', help);
    container.appendChild(helpField);
};

const syncDropdownValue = (select, value) => {
    select.value = value;

    try {
        const jq = typeof $ !== 'undefined' ? $ : null;
        jq?.(select).dropdown('set selected', value);
    } catch (e) {
        select.value = value;
    }
};

const initializeHeaderNameDropdowns = root => {
    try {
        const jq = typeof $ !== 'undefined' ? $ : null;
        jq?.(root.querySelectorAll('.http-request-header-name')).dropdown({
            duration: 0,
            fullTextSearch: true,
            allowAdditions: true,
            hideAdditions: false,
            forceSelection: false
        });
    } catch (e) {}
};

const createOpenApiSearch = mxResources => {
    const search = document.createElement('div');
    search.id = 'openApisSearch';
    search.className = 'ui short search field fluid';

    const inputContainer = document.createElement('div');
    inputContainer.className = 'ui left icon input';

    const input = document.createElement('input');
    input.className = 'prompt';
    input.type = 'text';
    input.placeholder = getResource(mxResources, 'openApisSearch');

    const icon = document.createElement('i');
    icon.className = 'cogs icon';

    inputContainer.appendChild(input);
    inputContainer.appendChild(icon);
    search.appendChild(inputContainer);

    return search;
};

const createMethodOptions = () => httpMethods.map(method => ({
    value: method,
    name: method
}));

const createHeaderNameOptions = () => wellKnownRequestHeaders.map(header => ({
    value: header,
    name: header
}));

const createBodyModeOptions = mxResources => [{
    value: bodyModeEmpty,
    name: getResource(mxResources, 'httpBodyModeEmpty')
}, {
    value: bodyModeText,
    name: getResource(mxResources, 'httpBodyModeText')
}, {
    value: bodyModeJson,
    name: getResource(mxResources, 'httpBodyModeJson')
}];

const createHttpTaskForm = ({
                                task = {},
                                taskErrors = {},
                                editable = true,
                                mxResources,
                                onHttpRequestChange = () => {}
                            } = {}) => {
    let httpRequest = normalizeHttpRequest(task.httpRequest);
    let bodyMode = bodyModeFor(httpRequest.body);
    let bodyJsonValid = true;
    let bodyJsonError = null;
    let bodyJsonEditorMount = null;
    let bodyJsonCodeMirrorModule = null;
    let headersField = null;
    let bodyField = null;
    let jsonExpressionHelp = null;

    const container = document.createElement('div');
    container.id = 'httpRequestEditor';
    container.className = 'ui field';

    container.appendChild(createOpenApiSearch(mxResources));

    appendFieldError(container, taskErrors?.message || getDirectFieldError(taskErrors?.fields?.httpRequest), {className: 'http-request-error'});

    const urlMethodField = document.createElement('div');
    urlMethodField.id = 'httpRequestUrlMethodField';
    urlMethodField.className = 'required field';

    const urlLabel = document.createElement('label');
    urlLabel.setAttribute('for', 'httpRequestUrl');
    urlLabel.textContent = getResource(mxResources, 'httpUrl');
    urlMethodField.appendChild(urlLabel);

    const urlMethodInput = document.createElement('div');
    urlMethodInput.id = 'httpRequestUrlMethodInput';
    urlMethodInput.className = 'ui fluid labeled input';

    const methodSelect = document.createElement('select');
    methodSelect.id = 'httpRequestMethod';
    methodSelect.name = 'httpRequest.method';
    methodSelect.className = 'ui compact selection dropdown label httpRequestMethod http-request-method-label';
    createMethodOptions().forEach(option => {
        const optionElement = document.createElement('option');
        optionElement.value = option.value;
        optionElement.textContent = option.name;
        if (option.value === getDisplayMethod(httpRequest)) {
            optionElement.selected = true;
            optionElement.setAttribute('selected', '');
        }
        methodSelect.appendChild(optionElement);
    });
    methodSelect.value = getDisplayMethod(httpRequest);
    methodSelect.addEventListener('change', event => emitHttpRequest({method: event.target.value}));

    const urlInput = document.createElement('input');
    urlInput.id = 'httpRequestUrl';
    urlInput.name = 'httpRequest.url';
    urlInput.type = 'text';
    urlInput.value = httpRequest.url || '';
    urlInput.placeholder = 'https://api.example.com/resource';
    urlInput.addEventListener('change', event => emitHttpRequest({url: event.target.value}));

    urlMethodInput.appendChild(methodSelect);
    urlMethodInput.appendChild(urlInput);
    urlMethodField.appendChild(urlMethodInput);
    appendFieldError(urlMethodField, taskErrors?.fields?.httpRequest?.url);
    appendFieldError(urlMethodField, taskErrors?.fields?.httpRequest?.method);
    container.appendChild(urlMethodField);

    const bodyModeField = createDropdown({
        label: getResource(mxResources, 'httpBodyMode'),
        id: 'httpRequestBodyMode',
        name: 'httpRequest.bodyMode',
        value: bodyMode,
        error: taskErrors?.fields?.httpRequest?.body,
        options: createBodyModeOptions(mxResources)
    });
    const bodyModeSelect = bodyModeField.querySelector('select');
    bodyModeSelect.value = bodyMode;
    bodyModeSelect.addEventListener('change', event => {
        bodyMode = event.target.value;
        bodyJsonValid = true;
        bodyJsonError = null;
        if (bodyMode === bodyModeEmpty) {
            emitHttpRequest({body: null});
        } else if (bodyMode === bodyModeText) {
            emitHttpRequest({body: typeof httpRequest.body === 'string' ? httpRequest.body : stringifyBody(httpRequest.body)});
        } else {
            const bodyText = typeof httpRequest.body === 'string' ? httpRequest.body : stringifyBody(httpRequest.body);
            const parsed = parseJsonBody(bodyText);
            if (!isEmpty(bodyText) && parsed.valid) {
                emitHttpRequest({body: parsed.body});
            } else if (!parsed.valid) {
                bodyJsonValid = false;
                bodyJsonError = getResource(mxResources, 'httpBodyInvalidJson');
            }
        }
        renderBodyField();
    });
    container.appendChild(bodyModeField);

    const renderHeadersField = () => {
        const headersControl = createKeyValueMap({
            value: isPlainObject(httpRequest.headers) ? httpRequest.headers : {},
            keyName: 'httpRequest.headerName',
            valueName: 'httpRequest.headerValue',
            keyOptions: createHeaderNameOptions(),
            keyClassName: 'ui search selection dropdown label http-request-header-name',
            addLabel: getResource(mxResources, 'httpAddHeader'),
            removeLabel: getResource(mxResources, 'httpRemoveHeader'),
            addBlankRow: true,
            removeButtonVisibility: editable ? 'focus' : 'none',
            onRender: headerControl => {
                if (editable) {
                    initializeHeaderNameDropdowns(headerControl);
                }
            },
            onChange: headers => emitHttpRequest({headers})
        });
        const nextHeadersField = createField({
            label: getResource(mxResources, 'httpHeaders'),
            error: taskErrors?.fields?.httpRequest?.headers,
            fieldElement: headersControl
        });
        if (headersField) {
            headersField.replaceWith(nextHeadersField);
        } else {
            container.insertBefore(nextHeadersField, bodyModeField);
        }
        headersField = nextHeadersField;
    };

    const mountBodyJsonEditor = (codeEditorModule, value = stringifyBody(httpRequest.body)) => {
        bodyJsonCodeMirrorModule = codeEditorModule || bodyJsonCodeMirrorModule;

        if (!bodyJsonCodeMirrorModule || !bodyJsonEditorMount) {
            return;
        }

        if (bodyJsonEditorMount.dataset.editorMounted === 'true') {
            return;
        }

        bodyJsonEditorMount.dataset.editorMounted = 'true';
        bodyJsonCodeMirrorModule.codeEditor({
            callbackFn: editor => {
                editor.on('blur', codeMirror => {
                    const bodyText = codeMirror.getValue();
                    codeMirror.setOption?.('lint', !isEmpty(bodyText));

                    const parsed = parseJsonBody(bodyText);
                    if (parsed.valid) {
                        bodyJsonValid = true;
                        bodyJsonError = null;
                        emitHttpRequest({body: parsed.body});
                        renderBodyField();
                    } else {
                        bodyJsonValid = false;
                        bodyJsonError = getResource(mxResources, 'httpBodyInvalidJson');
                        renderBodyField(bodyText);
                    }
                });

                const wrapper = editor.getWrapperElement();
                wrapper.classList.add('ui', 'segment');
                bodyJsonEditorMount.replaceWith(wrapper);
                editor.refresh();
            },
            errorFn: () => {
                bodyJsonEditorMount.className = 'ui message error visible';
                bodyJsonEditorMount.textContent = getResource(mxResources, 'errorLoadingEditor');
            },
            options: {
                readOnly: !editable ? 'nocursor' : false,
                mode: 'application/json',
                lint: !isEmpty(value),
                foldGutter: true,
                gutters: ['CodeMirror-lint-markers', 'CodeMirror-foldgutter'],
                value
            }
        });
    };

    const renderBodyField = displayBodyText => {
        bodyJsonEditorMount = null;

        if (bodyMode === bodyModeEmpty) {
            if (bodyField) {
                bodyField.remove();
                bodyField = null;
            }
            return;
        }

        const bodyText = typeof displayBodyText === 'undefined' ? stringifyBody(httpRequest.body) : displayBodyText;
        if (bodyMode === bodyModeJson) {
            bodyJsonEditorMount = document.createElement('div');
            bodyJsonEditorMount.id = 'httpRequestBodyJsonEditor';
            bodyJsonEditorMount.className = 'ui active centered inline loader';

            const bodyJsonField = createField({
                label: getResource(mxResources, 'httpBody'),
                error: bodyJsonError ? null : taskErrors?.fields?.httpRequest?.body,
                fieldElement: bodyJsonEditorMount
            });
            if (bodyJsonError) {
                bodyJsonField.className += ' error';
                appendFieldError(bodyJsonField, bodyJsonError, {className: 'http-body-json-error'});
            }
            if (bodyField) {
                bodyField.replaceWith(bodyJsonField);
            } else {
                container.appendChild(bodyJsonField);
            }
            bodyField = bodyJsonField;
            mountBodyJsonEditor(bodyJsonCodeMirrorModule, bodyText);
            return;
        }

        const bodyTextareaField = createTextareaField({
            label: getResource(mxResources, 'httpBody'),
            id: 'httpRequestBody',
            name: 'httpRequest.body',
            rows: 5,
            value: bodyText,
            error: taskErrors?.fields?.httpRequest?.body
        });
        const textarea = bodyTextareaField.querySelector('textarea');
        textarea.value = bodyText;
        textarea.addEventListener('change', event => {
            bodyJsonValid = true;
            bodyJsonError = null;
            emitHttpRequest({body: event.target.value});
        });
        if (bodyField) {
            bodyField.replaceWith(bodyTextareaField);
        } else {
            container.appendChild(bodyTextareaField);
        }
        bodyField = bodyTextareaField;
    };

    const syncStructuredFields = () => {
        urlInput.value = httpRequest.url || '';
        syncDropdownValue(methodSelect, getDisplayMethod(httpRequest));
        bodyMode = bodyModeFor(httpRequest.body);
        syncDropdownValue(bodyModeSelect, bodyMode);
        bodyJsonValid = true;
        bodyJsonError = null;
        renderHeadersField();
        renderBodyField();
    };

    function emitHttpRequest(patch) {
        const nextHttpRequest = normalizeHttpRequest({
            ...httpRequest,
            ...patch
        });

        if (isEqual(httpRequest, nextHttpRequest)) {
            return;
        }

        httpRequest = nextHttpRequest;
        task.httpRequest = cloneValue(httpRequest);
        onHttpRequestChange(cloneValue(httpRequest));
    }

    renderHeadersField();
    renderBodyField();
    appendJsonExpressionHelpToContainer(container, jsonExpressionHelp);

    if (!editable) {
        disableFormControls(container);
    }

    return {
        element: container,
        getHttpRequest: () => cloneValue(httpRequest),
        setHttpRequest: value => {
            httpRequest = normalizeHttpRequest(value);
            task.httpRequest = cloneValue(httpRequest);
            syncStructuredFields();
        },
        isValid: () => bodyJsonValid,
        mountBodyJsonEditor,
        appendJsonExpressionHelp: help => {
            jsonExpressionHelp = help;
            appendJsonExpressionHelpToContainer(container, jsonExpressionHelp);
        }
    };
};

export {
    bodyModeEmpty,
    bodyModeJson,
    bodyModeText,
    createHttpTaskForm,
    defaultHttpRequest,
    normalizeHttpRequest
};
